<template>
  <div class="contest-entry-novel-select-overlay" @click.self="onCancel">
    <div class="modal" role="dialog" aria-modal="true" :aria-label="title">
      <div class="modal-header">
        <h2 class="modal-title">{{ title }}</h2>
        <button type="button" class="close-button" :aria-label="$t('common.close')" @click="onCancel">
          <IconClose class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <LoadingSpinner v-if="loading" size="small" variant="inline" />
        <p v-else-if="listItems.length === 0" class="empty-message">{{ $t("contestEntryForm.step2.novelSelect.empty") }}</p>
        <ul v-else class="novel-list">
          <li v-for="item in listItems" :key="item.novelId">
            <button
              type="button"
              class="novel-item"
              :class="{ selected: selectedNovelId === item.novelId }"
              @click="onSelectNovel(item.novelId)"
            >
              <ImageView class="novel-cover" :src="item.image" />
              <div class="novel-meta">
                <p class="novel-title">{{ item.title }}</p>
              </div>
            </button>
          </li>
        </ul>
      </div>

      <div class="modal-footer">
        <button type="button" class="confirm-button" :disabled="!selectedNovelId" @click="onConfirm">
          {{ $t("contestEntryForm.step2.novelSelect.confirm") }}
        </button>
        <button type="button" class="cancel-button" @click="onCancel">
          {{ $t("contestEntryForm.step2.novelSelect.cancel") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { Novel } from "@/lib/models";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import ImageView from "@/components/atoms/ImageView.vue";
import { getNovelCoverImageSrc } from "@/lib/utils/contestEntryNovelSelection";
import IconClose from "~icons/figma/Close";

export type ContestEntryNovelSelectDialogResult = {
  novelId: string;
  title: string;
  image: string;
  description: string;
};

type NovelListItem = {
  novelId: string;
  title: string;
  image: string;
};

interface Props {
  title: string;
  novels: Novel[];
  initialNovelId?: string;
}

export default Vue.extend({
  components: {
    LoadingSpinner,
    ImageView,
    IconClose,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    novels: {
      type: Array as PropType<Novel[]>,
      default: () => [],
    },
    initialNovelId: {
      type: String,
      default: "",
    },
  },
  data(): {
    loading: boolean;
    selectedNovelId: string;
    listItems: NovelListItem[];
  } {
    return {
      loading: true,
      selectedNovelId: this.initialNovelId ?? "",
      listItems: [],
    };
  },
  created() {
    this.loadNovelItems();
  },
  methods: {
    loadNovelItems() {
      this.listItems = this.novels.map((novel) => ({
        novelId: novel.novelId,
        title: novel.title?.trim() || (this.$t("common.untitled") as string),
        image: getNovelCoverImageSrc(novel),
      }));
      this.loading = false;
    },
    onSelectNovel(novelId: string) {
      this.selectedNovelId = novelId;
    },
    onCancel() {
      this.$close(null);
    },
    onConfirm() {
      const selected = this.novels.find((novel) => novel.novelId === this.selectedNovelId);
      if (!selected) return;

      const result: ContestEntryNovelSelectDialogResult = {
        novelId: selected.novelId,
        title: selected.title?.trim() || (this.$t("common.untitled") as string),
        image: getNovelCoverImageSrc(selected),
        description: selected.description ?? "",
      };
      this.$close(result);
    },
  },
});

export type ContestEntryNovelSelectDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.contest-entry-novel-select-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background-color: var(--color-overlay);
  backdrop-filter: var(--backdrop-blur-md);
}

.modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  max-height: min(780px, calc(100vh - 48px));
  border-radius: var(--radius-lg);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
}

.modal-header {
  position: relative;
  flex-shrink: 0;
  padding: 20px 48px 16px;
}

.modal-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
    border-radius: var(--radius-xs);
  }
}

.close-icon {
  font-size: 16px;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 0 8px;
}

.empty-message {
  margin: 0;
  padding: 24px 16px;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  text-align: center;
}

.novel-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.novel-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  min-height: 75px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  text-align: left;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--color-focus-ring);
  }

  &.selected {
    background-color: var(--color-surface-base);
  }
}

.novel-cover {
  flex-shrink: 0;
  width: 40px;
  height: 59px;

  :deep(.image) {
    width: 40px;
    height: 59px;
    object-fit: cover;
  }
}

.novel-meta {
  flex: 1;
  min-width: 0;
  padding-top: 8px;
}

.novel-title {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  word-break: break-word;
}

.modal-footer {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

.confirm-button,
.cancel-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.confirm-button {
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;
  }
}

.cancel-button {
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
}
</style>
